// src/pages/document/DocumentCategoryCreatePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { FormRow } from '@/components/form';

/**
 * Document category create page.
 */
export const DocumentCategoryCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useDocument();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    sort_order: '',
    parent: '',
    default_numbering_scheme: '',
    default_document_type: '',
  });

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await store.CreateCategory({
        ...formData,
        sort_order: formData.sort_order ? Number(formData.sort_order) : 0,
      } as any);
      navigate('/documents/categories');
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/documents/categories" className="text-muted-foreground hover:text-foreground">
            {t('document.categories')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.createCategory')}</h1>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <form onSubmit={handleSubmit} noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('document.categoryDetails')}</CardTitle>
            <CardDescription>{t('document.fillCategoryInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            <FormRow label={t('document.categoryFieldName')} htmlFor="cat-name">
              <input
                id="cat-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('document.categoryFieldNamePlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.categoryFieldDescription')} htmlFor="cat-desc">
              <input
                id="cat-desc"
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('document.categoryFieldDescriptionPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.categoryFieldSlug')} htmlFor="cat-slug">
              <input
                id="cat-slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder={t('document.categoryFieldSlugPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.categoryFieldSortOrder')} htmlFor="cat-sort" hint={t('document.categoryFieldSortOrderHint')}>
              <span className="inline-flex items-center gap-2">
                <input
                  id="cat-sort"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => handleChange('sort_order', e.target.value)}
                  placeholder="—"
                  className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {formData.sort_order && (
                  <button
                    type="button"
                    onClick={() => handleChange('sort_order', '')}
                    aria-label={t('common.clear')}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                )}
              </span>
            </FormRow>

            <FormRow label={t('document.categoryFieldParent')} htmlFor="cat-parent" hint={t('document.categoryFieldParentHint')}>
              <input
                id="cat-parent"
                type="text"
                value={formData.parent}
                onChange={(e) => handleChange('parent', e.target.value)}
                placeholder={t('document.categoryFieldParentPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.categoryFieldDefaultNumberingScheme')} htmlFor="cat-scheme">
              <input
                id="cat-scheme"
                type="text"
                value={formData.default_numbering_scheme}
                onChange={(e) => handleChange('default_numbering_scheme', e.target.value)}
                placeholder={t('document.categoryFieldDefaultNumberingSchemePlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('document.categoryFieldDefaultDocumentType')} htmlFor="cat-doctype">
              <input
                id="cat-doctype"
                type="text"
                value={formData.default_document_type}
                onChange={(e) => handleChange('default_document_type', e.target.value)}
                placeholder={t('document.categoryFieldDefaultDocumentTypePlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/documents/categories">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting || !formData.name.trim()}>
              {isSubmitting ? t('common.creating') : t('document.createCategory')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default DocumentCategoryCreatePage;
